import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../api/client';
import { useMe, useMeetings, useOpen, useOrg, usePerson, usePersonDecisions } from '../api/queries';
import { BackLink, Broken, Card, Empty, Grade, Pill, Rows, SkeletonRows, Tabs, Who } from '../ui';
import { ago, between, clock, dateLabel, duration, since, weekdayClock } from '../lib/time';
import { initialsOf } from '../ui';
import { companyOf, domainOf } from '../lib/company';
import { useUrlParam } from '../lib/url-tab';

const WEEK = 7 * 24 * 60 * 60 * 1000;

/** Board 10, second half — one person: where you stand with them, then what you were in together. */
export function PersonPage(): ReactElement {
  const { email = '' } = useParams();
  // In the address, replaced rather than pushed: Back leaves the person.
  const [sideParam, setSide] = useUrlParam('side');
  const side = sideParam === 'mine' ? 'mine' : sideParam === 'theirs' ? 'theirs' : null;
  const who = usePerson(email);
  const open = useOpen({ person: email });
  const meetings = useMeetings({ person: email });
  const decisions = usePersonDecisions(email);
  const me = useMe();
  const org = useOrg();
  const home = me.data
    ? { domain: domainOf(me.data.email), name: org.data?.name ?? 'Your workspace' }
    : null;

  if (who.isPending)
    return (
      <div className="page">
        <SkeletonRows rows={3} />
      </div>
    );
  if (who.isError) {
    const unknown = who.error instanceof ApiError && who.error.status === 404;
    return (
      <div className="page">
        <Card flush>
          {unknown ? (
            <Empty
              lead="You have not been in a meeting with this person."
              action={
                <Link to="/people" className="btn">
                  People
                </Link>
              }
            />
          ) : (
            <Broken what="This person" retry={() => void who.refetch()} />
          )}
        </Card>
      </div>
    );
  }
  const p = who.data;
  const guest = Boolean(home && p.company && p.company.toLowerCase() !== home.domain);
  const together = (meetings.data ?? []).reduce((s, m) => s + between(m.startedAt, m.endedAt), 0);
  const items = (open.data?.items ?? []).filter((i) =>
    side === 'mine'
      ? i.direction === 'I_OWE'
      : side === 'theirs'
        ? i.direction === 'OWED_TO_ME' && i.ownerEmail?.toLowerCase() === email.toLowerCase()
        : i.direction !== 'SOMEONE_ELSE',
  );

  return (
    <div className="page stack-lg">
      <div className="toolbar">
        <BackLink to="/people">People</BackLink>
        <span className="crumb">{p.name ?? p.email}</span>
      </div>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <div className="cluster" style={{ gap: 14, flexWrap: 'nowrap' }}>
          <span className="avatar xl">{initialsOf(p.name, p.email)}</span>
          <div className="stack" style={{ gap: 2 }}>
            <h1 className="t-title">{p.name ?? p.email}</h1>
            <p className="mono">
              {companyOf(p.company, home) ?? p.email}
              {guest ? ' · guest' : ''} · last spoke {since(p.lastMetAt)} · {p.meetingCount}{' '}
              {p.meetingCount === 1 ? 'meeting' : 'meetings'} · {duration(together)} together
            </p>
          </div>
        </div>
      </div>

      {/* Which side of what is open, as tabs — not figures in boxes that read
          as buttons (Vladimir, 2026-09-24). */}
      <Tabs
        label="Which side"
        value={side ?? 'all'}
        onChange={(k) => setSide(k === 'all' ? null : k)}
        items={[
          {
            key: 'all' as const,
            label: 'Open between you',
            count: p.balance.iOwe + p.balance.theyOwe,
          },
          { key: 'mine' as const, label: 'You owe', count: p.balance.iOwe },
          { key: 'theirs' as const, label: 'Owes you', count: p.balance.theyOwe },
        ]}
      />
      <Card flush>
        {open.isPending ? (
          <SkeletonRows rows={2} dense />
        ) : items.length === 0 ? (
          <Empty tone="ok" lead="Nothing open on this side." />
        ) : (
          <Rows>
            {items.map((i) => (
              <div key={i.id} className="row">
                <div className="row-main">
                  <span className="row-title">{i.text}</span>
                  <span className="row-sub">
                    {side !== 'theirs' && i.ownerEmail ? (
                      <Who email={i.ownerEmail} name={i.ownerName} me={i.direction === 'I_OWE'} />
                    ) : null}
                    <Link
                      to={`/m/${i.meeting.id}`}
                      className="mono"
                      style={{ color: 'var(--faint)' }}
                    >
                      {i.meeting.title} · {weekdayClock(i.meeting.startedAt)}
                    </Link>
                    <span
                      className={`mono ${Date.now() - Date.parse(i.createdAt) > WEEK ? 'amber' : ''}`}
                    >
                      {ago(i.createdAt)}
                    </span>
                    <Grade value={i.grade} />
                  </span>
                </div>
              </div>
            ))}
          </Rows>
        )}
      </Card>

      <Card
        title="Decisions together"
        flush
        aside={decisions.data ? <span className="mono">{decisions.data.length}</span> : null}
      >
        {decisions.isPending ? (
          <SkeletonRows rows={2} dense />
        ) : !decisions.data?.length ? (
          <Empty lead="Nothing settled between you yet." />
        ) : (
          <Rows>
            {decisions.data.map((d) => (
              <Link key={d.id} to={`/m/${d.meetingId}`} className="row dense">
                <Pill tone={d.status === 'STANDS' ? 'green' : undefined}>
                  {d.status === 'STANDS' ? 'stands' : d.status.toLowerCase()}
                </Pill>
                <span
                  className="row-title grow"
                  style={
                    d.status === 'SUPERSEDED'
                      ? { textDecoration: 'line-through', color: 'var(--muted)' }
                      : undefined
                  }
                >
                  {d.text}
                </span>
                <Grade value={d.grade} />
              </Link>
            ))}
          </Rows>
        )}
      </Card>

      <Card
        title="Meetings together"
        flush
        aside={meetings.data ? <span className="mono">{meetings.data.length}</span> : null}
      >
        {meetings.isPending ? (
          <SkeletonRows rows={3} />
        ) : meetings.isError ? (
          <Broken what="The meetings" retry={() => void meetings.refetch()} />
        ) : meetings.data.length === 0 ? (
          <Empty lead="None you can open." />
        ) : (
          <Rows>
            {meetings.data.map((m) => (
              <Link key={m.id} to={`/m/${m.id}`} className="row">
                <span className="mono" style={{ width: 110 }}>
                  {dateLabel(m.startedAt)} {clock(m.startedAt)}
                </span>
                <div className="row-main">
                  <span className="row-title">{m.title}</span>
                  <span className="row-sub">
                    {m.commitmentCount} commitments · {m.decisionCount} decisions
                  </span>
                </div>
                <span className="mono">{duration(between(m.startedAt, m.endedAt))}</span>
              </Link>
            ))}
          </Rows>
        )}
      </Card>
    </div>
  );
}
